import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

export default function VerifyEmailPage() {
  const { t } = useLanguage();
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get('email') || '');
  const [code, setCode] = useState(sp.get('dev_code') || '');
  const devCode = sp.get('dev_code');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/auth/verify-email', { email, code });
      loginWithToken(r.data.token, r.data.user);
      toast.success('Email verified');
      // If there is a pending company registration (business flow), submit it now
      const pendingCompanyRaw = localStorage.getItem('concorde_pending_company');
      if (pendingCompanyRaw && (r.data.user?.account_type === 'business' || sp.get('next') === 'business-portal')) {
        try {
          const pendingCompany = JSON.parse(pendingCompanyRaw);
          await api.post('/b2b/company/register', pendingCompany);
          localStorage.removeItem('concorde_pending_company');
          toast.success('Company submitted for verification');
          nav('/business-portal');
          return;
        } catch (err) {
          localStorage.removeItem('concorde_pending_company');
          toast.error('Company registration failed: ' + (err.response?.data?.detail || 'unknown'));
          nav('/business-portal/register');
          return;
        }
      }
      nav('/wallet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      const r = await api.post('/auth/resend-code', { email });
      toast.success('Code sent');
      if (r.data.dev_code) setCode(r.data.dev_code);
    } catch (err) {
      toast.error('Could not resend');
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src="/logo.png" alt="Concorde Financial Limited" className="h-11 w-auto mx-auto" />
          </Link>
        </div>
        <Card className="bg-white">
          <CardContent className="p-8">
            <div className="h-12 w-12 rounded-xl bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center mb-4"><Mail className="h-6 w-6 text-[hsl(var(--brand-navy))]" /></div>
            <h1 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('auth.verify_title')}</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{t('auth.verify_subtitle')}</p>
            {devCode && (
              <div className="mt-4 rounded-xl border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/10 p-3 text-sm">
                <div className="font-semibold text-[hsl(var(--brand-navy))]">Dev code (email service in mock mode):</div>
                <div className="font-mono text-lg tracking-widest">{devCode}</div>
              </div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label>{t('auth.email')}</Label><Input required type="email" data-testid="verify-email-input" value={email} onChange={e => setEmail(e.target.value)} /></div>
              <div><Label>{t('auth.code')}</Label><Input required data-testid="verify-code-input" value={code} onChange={e => setCode(e.target.value)} className="tracking-widest text-lg" /></div>
              <Button type="submit" disabled={busy} data-testid="verify-submit-button" className="w-full bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">{t('auth.submit_verify')}</Button>
              <Button type="button" variant="ghost" onClick={resend} className="w-full">{t('auth.resend')}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
